import { Fragment, useCallback, useMemo, useRef } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import TextFit from 'components/uiLibrary/TextFit';

import CommonTrans from 'components/Globals/CommonTrans';
import EntityName from 'components/Globals/EntityName';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import useNavigate from 'utils/hooks/useNavigate';
import useTruncatedText from 'utils/hooks/useTruncatedText';
import { getPageH1LinkProps } from 'utils/pageH1';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES, FILTER_TYPES, OPERATOR_LABELS, ROUTE_PART_TYPE } from 'operabase-router/constants';

import classes from './PageH1.module.scss';

const H1TextPart = ({ subPart, index, subIndex, filtersCount, nameRefs, t, isMobile, handleCloseFilter }) => {
  const isNotReserved = subPart?.type !== ROUTE_PART_TYPE.RESERVED;
  const isNotStagingType = subPart?.filterTypeKey !== FILTER_TYPES.STAGING_TYPE;
  const showCloseIcon = filtersCount > 1 && subPart?.filterTypeKey && isNotReserved && isNotStagingType && index > 0;
  const refKey = `${index}-${subIndex}`;
  const nameWidth = (nameRefs.current[refKey]?.offsetWidth || 0) + 20;
  const getTruncatedText = useTruncatedText();

  const headline = useMemo(
    () =>
      subPart.headline?.map(h => {
        const parts = t(h).split(', ');

        if (parts.length <= 2) {
          return parts.join(', ');
        }

        return getTruncatedText(t(h).split(','), nameWidth, {
          fontSize: 12,
          fontWeight: 400,
        }).text;
      }) || [],
    [subPart.headline, t, nameWidth, getTruncatedText],
  );

  return (
    <Fragment key={subIndex}>
      {isMobile && (subPart?.operator || subPart?.subTitle) && <span className={classes.title_break} />}
      {subPart?.operator && <span className={classes.title_operator}> {t(OPERATOR_LABELS[subPart.operator])} </span>}
      {subPart?.subTitle && (
        <span className={classes.title_subtitle}>
          {index > 0 && ' '}
          <CommonTrans i18nKey={subPart.subTitle} components={{ b: <b /> }} ns="NS_APP_GLOBALS" />{' '}
        </span>
      )}
      <span
        className={classnames(classes.title_part, {
          [classes.title_part__with_click]: !!subPart.onClick,
        })}
        data-headline={headline.join(' | ')}
        onClick={subPart.onClick}
        style={{ '--headline-width': `${nameWidth}px` }}
      >
        <span ref={el => (nameRefs.current[refKey] = el)}>{t(subPart.name)}</span>
        {subPart.composers?.length > 0 && (
          <>
            {', '}
            <span className={classes.title_composers}>
              {subPart.composers?.map(composer => (
                <EntityName
                  name={composer.shortName}
                  entityType={composer.identifier.entityType}
                  entity={{ id: composer.identifier.entityId, name: composer.name }}
                />
              ))}
            </span>
          </>
        )}
        <div className={classes.title_subpart_container}>
          {subPart.attributes?.length > 0 && (
            <span className={classes.title_subpart} style={{ '--subpart-max-width': `${nameWidth}px` }}>
              <span className={classes.title_subpart__enclosure}>{' ('}</span>
              {subPart.attributes?.map(h => t(h)).join(' | ')}
              <span className={classes.title_subpart__enclosure}>)</span>
            </span>
          )}
          {subPart.roles?.map(role => (
            <div className={classes.title_subpart}>
              <span className={classes.title_subpart__enclosure}>{', '}</span>
              {role.designation}
              {role.department && ` (${role.department})`}
              {role.organization && ` - `}
              {role.organization && (
                <EntityName
                  name={role.organization.name}
                  entityType={ENTITY_TYPES.ORGANIZATION}
                  entity={role.organization}
                />
              )}
            </div>
          ))}
        </div>
      </span>
      {showCloseIcon && (
        <SpriteIcon
          className={classes.title_closeIcon}
          icon="cancel_outlined"
          size={16}
          onClick={() => handleCloseFilter(subPart)}
        />
      )}
    </Fragment>
  );
};

const RenderH1Text = ({ text, isMobile, t, handleCloseFilter, filtersCount }) => {
  const nameRefs = useRef({});

  return (
    <h1 className={classes.title}>
      {text?.map((part, index) => (
        <Fragment key={index}>
          {index > 0 && ' / '}
          {part?.map((subPart, subIndex) => (
            <H1TextPart
              subPart={subPart}
              index={index}
              subIndex={subIndex}
              filtersCount={filtersCount}
              nameRefs={nameRefs}
              t={t}
              isMobile={isMobile}
              handleCloseFilter={handleCloseFilter}
            />
          ))}
        </Fragment>
      ))}
    </h1>
  );
};

const PageH1 = ({ skipFilters = false, maxFontSize = 26, minFontSize = 14 }) => {
  const { obRouteContext } = useAppContext();
  const { isMobile } = useDeviceTypeLayouts();
  const navigate = useNavigate();
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { linkProps, h1Title } = obRouteContext;
  const { filters } = linkProps || {};

  const filtersCount = useMemo(() => {
    const allowedFilterKeys = [
      FILTER_TYPES.WHO,
      FILTER_TYPES.WHERE,
      FILTER_TYPES.WHAT,
      FILTER_TYPES.WORK,
      FILTER_TYPES.GENRE,
      FILTER_TYPES.DATE,
    ];

    const h1Filters = Object.fromEntries(Object.entries(filters).filter(([key]) => allowedFilterKeys.includes(key)));
    return Object.values(h1Filters).reduce((sum, value) => {
      if (Array.isArray(value)) {
        return sum + value.length;
      }

      if (value && typeof value === 'object') {
        return sum + 1;
      }

      return sum;
    }, 0);
  }, [filters]);
  const { titleParts, partsLength } = useMemo(() => {
    if (!h1Title || !Array.isArray(h1Title)) {
      return {
        titleParts: [],
        partsLength: 0,
      };
    }

    if (skipFilters) {
      return {
        titleParts: h1Title?.[0]?.length > 0 ? [h1Title?.[0]] : [],
        partsLength: h1Title?.length,
      };
    }

    return {
      titleParts: h1Title,
      partsLength: h1Title?.reduce((acc, part) => acc + part.length, 0),
    };
  }, [h1Title, skipFilters]);

  const handleCloseFilter = useCallback(
    part => {
      const updatedLinkProps = {
        ...linkProps,
        ...getPageH1LinkProps({ filters, part, filtersCount }),
      };
      navigate.to(navigate.getLinkProps(updatedLinkProps));
    },
    [filters, filtersCount, linkProps, navigate],
  );

  return (
    <div className={classes.root}>
      <TextFit
        text={titleParts}
        max={maxFontSize}
        min={partsLength > 2 ? minFontSize : maxFontSize}
        mode="multi"
        textRenderer={text => RenderH1Text({ text, isMobile, t, handleCloseFilter, filtersCount })}
      />
    </div>
  );
};

export default PageH1;
